import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import { auth, db, functions } from '@/core/config/firebase'; // Import the initialized v8 compat auth instance
import { setUserContext, clearUserContext } from '@/sentry';
import type { TherapistProfile, UserRole, UserRoleData } from '@/core/types/roles';
import { Logger } from '@/core/utils';
import { UserProfile, ProfileType } from '@/core/types/profiles';
import { profileManager } from '@/core/services';
import { sessionService } from '@/core/security/session.service';
import { webAuthGuardService, ReauthResult } from '@/core/security/web-auth-guard.service';
import { ReauthModal } from '@/shared/components/security/ReauthModal';
import { mfaService, MFALoginResolver } from '@/core/security/mfa.service';
import { trustedDeviceService } from '@/core/security/trusted-device.service';
import { TwoFactorModal } from '@/shared/components/security/TwoFactorModal';

// Cloud Functions
const linkOfflineAccountsFn = functions.httpsCallable('linkOfflineAccounts');
const checkLoginRateLimitFn = functions.httpsCallable('checkLoginRateLimit');
const clearLoginAttemptsFn = functions.httpsCallable('clearLoginAttempts');

const logger = Logger.getLogger('AuthProvider');

// The User type is now available on the global firebase object from the compat SDK
type FirebaseUser = any; // Using 'any' for simplicity, could be firebase.User

// Define a more specific user type for our context
interface AppUser {
    uid: string;
    email: string | null;
    displayName?: string;
    name?: string;
    photoURL?: string;
    role: UserRole;
    roleData?: UserRoleData;
    therapistProfile?: TherapistProfile;
    onboardingCompleted?: boolean;
    metadata?: Record<string, any>;
}

interface AuthContextType {
    currentUser: AppUser | null;
    loading: boolean;
    login: (email: string, pass: string) => Promise<void>;
    signup: (email: string, pass: string) => Promise<void>;
    logout: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;

    // NUEVO: Multi-rol support
    profiles: UserProfile[];
    currentProfile: UserProfile | null;
    switchProfile: (profileId: string) => Promise<void>;
    hasRole: (role: ProfileType) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
    const [loading, setLoading] = useState(true);

    // NUEVO: Estados para multi-rol
    const [profiles, setProfiles] = useState<UserProfile[]>([]);
    const [currentProfile, setCurrentProfile] = useState<UserProfile | null>(null);

    // 🔐 SEGURIDAD: Estado para modal de re-autenticación web
    const [reauthModalOpen, setReauthModalOpen] = useState(false);
    const [reauthReason, setReauthReason] = useState('');
    const [reauthResolve, setReauthResolve] = useState<((result: ReauthResult) => void) | null>(null);

    // 🔐 MFA: Estado para Multi-Factor Authentication durante login
    const [mfaPending, setMfaPending] = useState(false);
    const [mfaResolver, setMfaResolver] = useState<MFALoginResolver | null>(null);

    useEffect(() => {
        let userDocUnsubscribe: (() => void) | null = null;

        const unsubscribe = auth.onAuthStateChanged((user: FirebaseUser | null) => {
            if (user) {
                const userDocRef = db.collection('users').doc(user.uid);

                // Cancelar listener previo si existía
                if (userDocUnsubscribe) {
                    userDocUnsubscribe();
                }

                userDocUnsubscribe = userDocRef.onSnapshot(
                    (snapshot: any) => {
                        // Si el documento no existe, crearlo automáticamente
                        // IMPORTANTE: Verificamos dos veces para evitar condiciones de carrera
                        if (!snapshot.exists) {
                            logger.info('User document does not exist in snapshot, verifying...', { userId: user.uid });
                            
                            // Usar una función async separada para no bloquear
                            const createUserDoc = async () => {
                                try {
                                    // ⚠️ CRÍTICO: Doble verificación para evitar sobrescribir
                                    // El snapshot puede estar desactualizado debido a cache o lag de red
                                    const freshDoc = await userDocRef.get();
                                    
                                    if (freshDoc.exists) {
                                        // El documento SÍ existe - NO creamos nada
                                        // Esto puede pasar si:
                                        // 1. El onboarding de terapeuta lo creó justo ahora
                                        // 2. Hay un delay de sincronización de cache
                                        // 3. Otra pestaña/dispositivo lo creó
                                        const existingData = freshDoc.data() || {};
                                        logger.info('User document exists after fresh get, skipping creation', { 
                                            userId: user.uid,
                                            existingRole: existingData.role,
                                            existingEmail: existingData.email
                                        });
                                        return; // El onSnapshot se activará de nuevo con los datos correctos
                                    }
                                    
                                    // El documento realmente NO existe - crear con rol adecuado
                                    logger.info('Confirmed document does not exist, creating...', { userId: user.uid });

                                    // Protección: si existe therapistAccounts/{uid}, es un terapeuta.
                                    // Esto evita que un terapeuta cuyo users-doc fue borrado quede
                                    // reseteado a 'user' cuando vuelva a iniciar sesión.
                                    let roleToSet: string = 'user';
                                    try {
                                        const therapistAccountDoc = await db.collection('therapistAccounts').doc(user.uid).get();
                                        if (therapistAccountDoc.exists) {
                                            roleToSet = 'therapist';
                                            logger.info('Found therapistAccounts doc, setting role therapist', { userId: user.uid });
                                        }
                                    } catch (checkErr) {
                                        logger.warn('Could not check therapistAccounts, defaulting to user', checkErr);
                                    }

                                    await userDocRef.set({
                                        email: user.email,
                                        name: user.displayName || 'Usuario',
                                        photo: user.photoURL || null,
                                        role: roleToSet,
                                        createdAt: new Date().toISOString(),
                                        updatedAt: new Date().toISOString()
                                    });
                                    
                                    // Intentar vincular cuentas offline
                                    try {
                                        const result = await linkOfflineAccountsFn({});
                                        if ((result.data as any)?.linkedCount > 0) {
                                            logger.info('Linked offline accounts', result.data);
                                        }
                                    } catch (linkError) {
                                        logger.warn('Could not link offline accounts', linkError);
                                    }
                                    
                                    logger.info('User document created successfully', { userId: user.uid });
                                } catch (createError) {
                                    logger.error('Failed to create user document', createError as Error);
                                }
                            };
                            
                            createUserDoc();
                            return; // El onSnapshot se volverá a disparar con el documento creado
                        }
                        
                        const userData = snapshot.data() || {};

                        const therapistProfile = userData.therapistProfile as TherapistProfile | undefined;
                        const roleFromDoc: UserRole = userData.role || 'user';

                        const roleDataFromDoc: UserRoleData | undefined = userData.roleData
                            ? {
                                ...userData.roleData,
                                therapistProfile:
                                    userData.roleData.therapistProfile ?? therapistProfile,
                            }
                            : therapistProfile
                                ? {
                                    role: roleFromDoc,
                                    therapistProfile,
                                }
                                : undefined;

                        const appUser: AppUser = {
                            uid: user.uid,
                            email: user.email,
                            displayName: userData.name || userData.displayName || user.displayName,
                            photoURL: userData.photo || userData.photoURL || user.photoURL,
                            role: roleFromDoc,
                            roleData: roleDataFromDoc,
                            therapistProfile,
                            onboardingCompleted: userData.onboardingCompleted ?? false,
                        };

                        setCurrentUser(appUser);
                        setUserContext(user.uid, user.email);

                        // Intentar vincular/reparar relaciones por email (idempotente).
                        // Importante: esto corrige casos donde therapistPatients tiene patientId incorrecto para este email.
                        // No bloquea la carga del usuario.
                        void (async () => {
                            try {
                                const result = await linkOfflineAccountsFn({});
                                const linkedCount = (result.data as any)?.linkedCount || 0;
                                if (linkedCount > 0) {
                                    logger.info('Linked/relinked therapist records by email', { linkedCount });
                                }
                            } catch (linkError) {
                                logger.warn('Could not link/relink therapist records by email', linkError);
                            }
                        })();

                        // NUEVO: Cargar perfiles del usuario
                        const activeProfileIds: string[] = Array.isArray(userData.activeProfiles)
                            ? userData.activeProfiles.filter((id: unknown): id is string => typeof id === 'string' && id.length > 0)
                            : [];

                        profileManager.getUserProfiles(user.uid).then(async userProfiles => {
                            let resolvedProfiles = userProfiles;

                            if (resolvedProfiles.length === 0 && activeProfileIds.length > 0) {
                                logger.warn('No se encontraron perfiles vía query. Aplicando fallback por IDs', { userId: user.uid, activeProfileIds });
                                resolvedProfiles = await profileManager.getProfilesByIds(activeProfileIds, user.uid);
                            }

                            setProfiles(resolvedProfiles);

                            // Establecer perfil activo
                            let nextProfile: UserProfile | null = null;
                            if (userData.currentProfileId) {
                                nextProfile = resolvedProfiles.find(p => p.id === userData.currentProfileId) || null;
                            }

                            if (!nextProfile && resolvedProfiles.length > 0) {
                                nextProfile = resolvedProfiles[0];
                            }

                            setCurrentProfile(nextProfile);
                        }).catch(error => {
                            logger.error('Failed to load user profiles', error, { userId: user.uid });
                            setProfiles([]);
                            setCurrentProfile(null);
                        });

                        setLoading(false);
                    },
                    (error: Error) => {
                        logger.error('Failed to subscribe to user document', error, { userId: user.uid });
                        setCurrentUser({
                            uid: user.uid,
                            email: user.email,
                            role: 'user',
                        });
                        setLoading(false);
                    }
                );
            } else {
                if (userDocUnsubscribe) {
                    userDocUnsubscribe();
                    userDocUnsubscribe = null;
                }
                setCurrentUser(null);
                setProfiles([]);
                setCurrentProfile(null);
                clearUserContext();
                setLoading(false);
            }
        });

        return () => {
            unsubscribe();
            if (userDocUnsubscribe) {
                userDocUnsubscribe();
            }
        };
    }, []);


    const login = async (email: string, pass: string): Promise<void> => {
        logger.info('Login attempt', { email });
        
        try {
            // 🔐 1. Verificar Rate Limiting antes de intentar login
            try {
                const rateLimitResult = await checkLoginRateLimitFn({ email });
                const data = rateLimitResult.data as { allowed: boolean; blocked: boolean; message?: string };
                
                if (!data.allowed) {
                    logger.warn('Login rate limited', { email, blocked: data.blocked });
                    throw new Error(data.message || 'Demasiados intentos. Por favor, espera unos minutos.');
                }
            } catch (rateLimitError: any) {
                // Si es un error de rate limit, propagarlo
                if (rateLimitError.message?.includes('intentos') || rateLimitError.message?.includes('bloqueado')) {
                    throw rateLimitError;
                }
                // Si es otro error (ej: función no disponible), continuar con el login
                logger.warn('Rate limit check failed, proceeding with login', { error: rateLimitError.message });
            }

            // 2. Intentar login
            const userCredential = await auth.signInWithEmailAndPassword(email, pass);
            logger.info('Login successful', { email, uid: userCredential.user?.uid });

            // 3. Limpiar intentos de rate limiting
            try {
                await clearLoginAttemptsFn({ email });
            } catch (clearError) {
                logger.warn('Failed to clear login attempts', { error: clearError });
            }

            // 4. Registrar sesión activa
            if (userCredential.user?.uid) {
                try {
                    await sessionService.registerSession(userCredential.user.uid);
                    logger.info('Session registered', { uid: userCredential.user.uid });
                } catch (sessionError) {
                    logger.warn('Failed to register session', { error: sessionError });
                }
            }

        } catch (error: any) {
            // 🔐 5. Manejar MFA requerido (Firebase dispara este error si tiene MFA)
            if (error.code === 'auth/multi-factor-auth-required') {
                logger.info('MFA required during login', { email });
                
                // Verificar si el dispositivo es de confianza
                const isTrusted = await trustedDeviceService.isDeviceTrusted();
                
                if (isTrusted) {
                    logger.info('Device is trusted, skipping MFA prompt');
                    // TODO: En producción, aún se debe verificar el MFA
                    // pero podríamos hacer el flujo automático
                }
                
                // Capturar el resolver de MFA
                const resolver = mfaService.handleMFARequired(error);
                
                if (resolver) {
                    setMfaResolver(resolver);
                    setMfaPending(true);
                    return; // No propagar el error, manejamos con modal
                } else {
                    logger.error('Could not get MFA resolver');
                    throw new Error('Error de autenticación de dos factores');
                }
            }
            
            logger.error('Login failed', error as Error, { email });
            throw error;
        }
    };

    const signup = async (email: string, pass: string): Promise<void> => {
        logger.info('Signup attempt', { email });
        try {
            // Using v8 compat syntax for createUserWithEmailAndPassword
            await auth.createUserWithEmailAndPassword(email, pass);
            logger.info('Signup successful', { email });
        } catch (error) {
            logger.error('Signup failed', error as Error, { email });
            throw error;
        }
    };

    // Exponer el userId globalmente para que los servicios puedan accederlo
    useEffect(() => {
        if (currentUser) {
            (window as any).__CURRENT_USER_ID__ = currentUser.uid;
        } else {
            (window as any).__CURRENT_USER_ID__ = null;
        }
    }, [currentUser]);

    const logout = async () => {
        logger.info('User logout', { userId: currentUser?.uid });
        
        // 🔐 Cerrar sesión activa antes de hacer signOut
        try {
            await sessionService.endCurrentSession();
            logger.info('Session ended');
        } catch (sessionError) {
            logger.warn('Failed to end session', { error: sessionError });
        }
        
        // Using v8 compat syntax for signOut
        await auth.signOut();
    };

    const resetPassword = async (email: string): Promise<void> => {
        logger.info('Password reset requested', { email });
        try {
            auth.languageCode = 'es';

            // Pre-check: Firebase Auth silently succeeds for unknown emails (anti-enumeration)
            // so we explicitly verify the account exists, otherwise the user gets misled
            // into thinking the email was sent.
            const methods = await auth.fetchSignInMethodsForEmail(email);
            if (!methods || methods.length === 0) {
                const err: Error & { code?: string } = new Error(
                    'No existe una cuenta con este correo. Si recibiste una invitación de tu terapeuta, primero debes aceptar el consentimiento y crear tu cuenta antes de poder recuperar la contraseña.'
                );
                err.code = 'auth/user-not-found';
                throw err;
            }

            const actionCodeSettings = {
                url: window.location.origin + '/#/login',
                handleCodeInApp: false,
            };

            await auth.sendPasswordResetEmail(email, actionCodeSettings);
            logger.info('Password reset email sent', { email });
        } catch (error) {
            logger.error('Password reset failed', error as Error, { email });
            throw error;
        }
    };

    // NUEVO: Función para cambiar de perfil
    const switchProfile = async (profileId: string): Promise<void> => {
        if (!currentUser) {
            logger.warn('Cannot switch profile: no user logged in');
            return;
        }

        logger.info('Switching profile', { userId: currentUser.uid, profileId });

        try {
            // Actualizar perfil activo en Firestore
            await profileManager.switchProfile(currentUser.uid, profileId);

            // Actualizar estado local
            const newProfile = profiles.find(p => p.id === profileId);
            if (newProfile) {
                setCurrentProfile(newProfile);
                logger.info('Profile switched successfully', { profileId, type: newProfile.type });
            }
        } catch (error) {
            logger.error('Failed to switch profile', error as Error, { profileId });
            throw error;
        }
    };

    // NUEVO: Verificar si usuario tiene un rol específico
    const hasRole = (role: ProfileType): boolean => {
        return profiles.some(p => p.type === role && p.isActive);
    };

    // 🔐 SEGURIDAD: Callback para mostrar modal de re-autenticación
    const showReauthModal = useCallback((reason: string): Promise<ReauthResult> => {
        return new Promise((resolve) => {
            setReauthReason(reason);
            setReauthResolve(() => resolve);
            setReauthModalOpen(true);
        });
    }, []);

    // 🔐 SEGURIDAD: Handler cuando el modal se completa
    const handleReauthComplete = useCallback((result: ReauthResult) => {
        setReauthModalOpen(false);
        if (reauthResolve) {
            reauthResolve(result);
            setReauthResolve(null);
        }
    }, [reauthResolve]);

    // 🔐 SEGURIDAD: Registrar callback en el servicio de seguridad web
    useEffect(() => {
        webAuthGuardService.setReauthModalCallback(showReauthModal);
        logger.info('Web auth guard callback registered');
    }, [showReauthModal]);

    // 🔐 MFA: Handler cuando se completa la verificación
    const handleMfaComplete = useCallback((success: boolean) => {
        setMfaPending(false);
        setMfaResolver(null);
        mfaService.cleanup();
        
        if (!success) {
            // Si no completó el MFA, hacer logout
            logger.warn('MFA not completed, logging out');
            logout();
        } else {
            logger.info('MFA completed successfully');
        }
    }, []);

    // 🔐 MFA: Handler para logout desde el modal de MFA
    const handleMfaLogout = useCallback(() => {
        setMfaPending(false);
        setMfaResolver(null);
        mfaService.cleanup();
        logout();
    }, []);

    const value = {
        currentUser,
        loading,
        login,
        signup,
        logout,
        resetPassword,

        // NUEVO: Multi-rol support
        profiles,
        currentProfile,
        switchProfile,
        hasRole
    };

    return (
        <AuthContext.Provider value={value}>
            {/* 🔐 MFA: Bloquear contenido si hay verificación MFA pendiente */}
            {mfaPending ? (
                <div className="min-h-screen bg-gradient-to-b from-stone-50 to-amber-50/30 flex items-center justify-center">
                    <div className="text-center p-8">
                        <div className="w-16 h-16 mx-auto mb-4 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
                        <p className="text-stone-600">Verificando acceso...</p>
                    </div>
                </div>
            ) : (
                children
            )}
            
            {/* 🔐 SEGURIDAD: Modal de re-autenticación para acceso a datos sensibles en web */}
            <ReauthModal
                isOpen={reauthModalOpen}
                reason={reauthReason}
                onComplete={handleReauthComplete}
            />

            {/* 🔐 MFA: Modal de Multi-Factor Authentication durante login */}
            <TwoFactorModal
                isOpen={mfaPending}
                resolver={mfaResolver}
                onComplete={handleMfaComplete}
                onLogout={handleMfaLogout}
            />
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};